import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AbmConnection {
  id: string;
  name: string;
  organization_id: string;
  customer_id: string | null;
  client_id: string;
  client_secret: string;
  access_token: string | null;
  refresh_token: string | null;
  server_token: string | null;
  token_expires_at: string | null;
  is_active: boolean | null;
  last_sync_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const useAbmConnections = (customerId?: string) => {
  const [connections, setConnections] = useState<AbmConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchConnections();
  }, [customerId]);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('abm_connections')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by customer if provided
      if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setConnections(data || []);
    } catch (err) {
      console.error('Error fetching ABM connections:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch ABM connections'));
      toast({
        title: 'Error',
        description: 'Failed to load Apple Business Manager connections',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createConnection = async (connectionData: {
    name: string;
    organization_id: string;
    customer_id?: string;
    client_id: string;
    client_secret: string;
    server_token?: string;
  }) => {
    try {
      const { data, error: insertError } = await supabase
        .from('abm_connections')
        .insert([connectionData])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      toast({
        title: 'Success',
        description: 'Apple Business Manager connection created successfully',
      });

      await fetchConnections();
      return data;
    } catch (err) {
      console.error('Error creating ABM connection:', err);
      toast({
        title: 'Error',
        description: 'Failed to create Apple Business Manager connection',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateConnection = async (id: string, updates: Partial<AbmConnection>) => {
    try {
      const { error: updateError } = await supabase
        .from('abm_connections')
        .update(updates)
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      toast({
        title: 'Success',
        description: 'ABM connection updated successfully',
      });

      await fetchConnections();
    } catch (err) {
      console.error('Error updating ABM connection:', err);
      toast({
        title: 'Error',
        description: 'Failed to update ABM connection',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deleteConnection = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('abm_connections')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      toast({
        title: 'Success',
        description: 'ABM connection deleted successfully',
      });

      await fetchConnections();
    } catch (err) {
      console.error('Error deleting ABM connection:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete ABM connection',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const testConnection = async (id: string) => {
    try {
      const { data, error: testError } = await supabase.functions.invoke('test-abm-connection', {
        body: { abm_connection_id: id },
      });

      if (testError) {
        throw testError;
      }

      toast({
        title: 'Success',
        description: 'Connection test successful',
      });

      return data;
    } catch (err) {
      console.error('Error testing ABM connection:', err);
      toast({
        title: 'Error',
        description: 'Connection test failed',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const getActiveConnections = () => {
    return connections.filter(conn => conn.is_active);
  };

  const getConnectionsByCustomer = (customerId: string) => {
    return connections.filter(conn => conn.customer_id === customerId);
  };

  return {
    connections,
    loading,
    error,
    fetchConnections,
    createConnection,
    updateConnection,
    deleteConnection,
    testConnection,
    getActiveConnections,
    getConnectionsByCustomer,
  };
};
